import { useState, useMemo, useCallback, useEffect } from 'react';
import { Form, Modal, message } from 'antd';
import { useCanCheck } from '@/ability/helpers';
import { useRoles } from './useRoles';
import { usePermissions } from './usePermissions';
import { useCreateRole } from './useCreateRole';
import { useUpdateRole } from './useUpdateRole';
import { useDeleteRole } from './useDeleteRole';
import { useRoleDetail } from './useRoleDetail';
import { formatCreated, formatLastUpdated } from '@utils/dateFormating.util';
import { Dropdown } from 'antd';
import { MoreOutlined } from '@ant-design/icons';

const PERM_TAB_ALL = 'all';

const capFirst = (s) => (s && s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** "open_market" -> "Open Market", "change_requests" -> "Change Requests" */
const formatModuleLabel = (s) =>
    s && typeof s === 'string' ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : s;

export const useRolesPage = () => {
    const [searchText, setSearchText] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editingRoleId, setEditingRoleId] = useState(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [permModuleTab, setPermModuleTab] = useState(PERM_TAB_ALL);
    const [permSearch, setPermSearch] = useState('');
    const [modalSelectedPermissionIds, setModalSelectedPermissionIds] = useState([]);
    const [selectedPredefinedRoleName, setSelectedPredefinedRoleName] = useState(null);
    const [form] = Form.useForm();

    const can = useCanCheck();
    const canView = can('saas.roles.view');
    const canCreate = can('saas.roles.create');
    const canUpdate = can('saas.roles.update');
    const canDelete = can('saas.roles.delete');

    const { data: roles = [] } = useRoles();
    const { data: permissionsPayload, isLoading: permissionsLoading } = usePermissions();
    const permissions = permissionsPayload?.permissions ?? [];
    const predefinedRoles = permissionsPayload?.predefinedRoles ?? [];
    const createRoleMutation = useCreateRole();
    const updateRoleMutation = useUpdateRole();
    const deleteRoleMutation = useDeleteRole();
    const { data: editingRole } = useRoleDetail(modalOpen && editingRoleId ? editingRoleId : null);

    const nameValue = Form.useWatch('name', form);

    const isRoleChanged = useMemo(() => {
        if (!editingRoleId || !editingRole) return true;

        const initialName = editingRole.name || '';
        const initialPerms = (editingRole.permissions || []).map((p) => p.id).sort();
        const currentPerms = [...modalSelectedPermissionIds].sort();

        const nameChanged = nameValue !== initialName;
        const permsChanged = JSON.stringify(initialPerms) !== JSON.stringify(currentPerms);

        return nameChanged || permsChanged;
    }, [editingRoleId, editingRole, nameValue, modalSelectedPermissionIds]);

    const selectedRoleName = useMemo(
        () => roles.find((r) => r.id === selectedRoleId)?.name ?? 'Role details',
        [roles, selectedRoleId],
    );

    const systemCount = useMemo(() => roles.filter((r) => r.isSystemRole).length, [roles]);
    const customCount = useMemo(() => roles.filter((r) => !r.isSystemRole).length, [roles]);

    const filteredRoles = useMemo(() => {
        let list = roles;
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            list = list.filter((r) => (r.name || '').toLowerCase().includes(q));
        }
        return list;
    }, [roles, searchText]);

    const tableData = useMemo(
        () =>
            filteredRoles.map((r) => ({
                key: r.id,
                id: r.id,
                name: r.name,
                isSystemRole: r.isSystemRole,
                type: r.isSystemRole ? 'System' : 'Custom',
                createdAt: r.createdAt,
                updatedAt: r.updatedAt,
                description: r.description,
            })),
        [filteredRoles],
    );

    useEffect(() => {
        if (!editingRoleId || !editingRole) return;
        setSelectedPredefinedRoleName(null);
        form.setFieldsValue({ name: editingRole.name });
        const ids = (editingRole.permissions || []).map((p) => p.id);
        setModalSelectedPermissionIds(ids);
    }, [editingRoleId, editingRole, form]);

    useEffect(() => {
        if (!modalOpen) return;
        if (editingRoleId) return;
        setSelectedPredefinedRoleName(null);
    }, [modalOpen, editingRoleId]);

    const predefinedRoleOptions = useMemo(
        () => predefinedRoles.map((r) => ({ value: r.name, label: r.name })),
        [predefinedRoles],
    );

    const applyPredefinedRole = useCallback(
        (roleName) => {
            const template = predefinedRoles.find((r) => r.name === roleName);
            setSelectedPredefinedRoleName(roleName || null);
            if (!template) return;

            form.setFieldsValue({ name: template.name });
            const ids = (template.permissions || []).map((p) => p.id).filter(Boolean);
            setModalSelectedPermissionIds(ids);
            setPermModuleTab(PERM_TAB_ALL);
            setPermSearch('');
        },
        [form, predefinedRoles],
    );

    const handleCreateRole = useCallback(() => {
        form.validateFields().then((values) => {
            createRoleMutation.mutate(
                {
                    name: values.name,
                    permissionIds: modalSelectedPermissionIds,
                },
                {
                    onSuccess: () => {
                        message.success('Role created');
                        setModalOpen(false);
                        form.resetFields();
                        setModalSelectedPermissionIds([]);
                        setEditingRoleId(null);
                        setSearchText('');
                        setPermModuleTab(PERM_TAB_ALL);
                        setPermSearch('');
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to create role');
                    },
                },
            );
        });
    }, [form, createRoleMutation, modalSelectedPermissionIds]);

    const handleUpdateRole = useCallback(() => {
        if (!editingRoleId) return;
        form.validateFields().then((values) => {
            updateRoleMutation.mutate(
                {
                    roleId: editingRoleId,
                    body: {
                        name: values.name,
                        permissionIds: modalSelectedPermissionIds,
                    },
                },
                {
                    onSuccess: () => {
                        message.success('Role updated');
                        setModalOpen(false);
                        form.resetFields();
                        setModalSelectedPermissionIds([]);
                        setEditingRoleId(null);
                        setSearchText('');
                        setPermModuleTab(PERM_TAB_ALL);
                        setPermSearch('');
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to update role');
                    },
                },
            );
        });
    }, [editingRoleId, form, updateRoleMutation, modalSelectedPermissionIds]);

    const permissionModules = useMemo(
        () => [...new Set(permissions.map((p) => p.module).filter(Boolean))].sort(),
        [permissions],
    );

    const permSliderTabItems = useMemo(
        () => [
            { key: PERM_TAB_ALL, label: 'All' },
            ...permissionModules.map((m) => ({
                key: m,
                label: formatModuleLabel(m),
            })),
        ],
        [permissionModules],
    );

    const filteredPermissionsForModal = useMemo(() => {
        let list = permissions;
        if (permModuleTab !== PERM_TAB_ALL) list = list.filter((p) => p.module === permModuleTab);
        if (permSearch.trim()) {
            const q = permSearch.toLowerCase();
            list = list.filter(
                (p) =>
                    (p.key || '').toLowerCase().includes(q) ||
                    (p.module || '').toLowerCase().includes(q) ||
                    (p.feature || '').toLowerCase().includes(q) ||
                    (p.action || '').toLowerCase().includes(q) ||
                    (p.description || '').toLowerCase().includes(q),
            );
        }
        return list;
    }, [permissions, permModuleTab, permSearch]);

    const modalPermissionTableData = useMemo(
        () =>
            filteredPermissionsForModal.map((p) => ({
                key: p.id,
                id: p.id,
                module: p.module ? formatModuleLabel(p.module) : '—',
                feature: p.feature ? formatModuleLabel(p.feature) : '—',
                action: p.action ? formatModuleLabel(p.action) : '—',
                description: p.description ? p.description : '—',
            })),
        [filteredPermissionsForModal],
    );

    /** Merge selection when table reports change (e.g. on tab switch table sends only current rows; preserve selection from other tabs) */
    const handlePermissionSelectionChange = useCallback((keys, currentTableIds) => {
        const currentSet = new Set(currentTableIds);
        setSelectedPredefinedRoleName(null);
        setModalSelectedPermissionIds((prev) => {
            const fromOtherTabs = prev.filter((id) => !currentSet.has(id));
            return [...fromOtherTabs, ...keys];
        });
    }, []);

    const toggleModalPermissionId = useCallback((id) => {
        setSelectedPredefinedRoleName(null);
        setModalSelectedPermissionIds((prev) => (prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]));
    }, []);

    const handleActionClick = useCallback(
        (menuInfo, record) => {
            if (menuInfo.key === 'delete') {
                if (!canDelete) return;
                const confirmInstance = Modal.confirm({
                    title: 'Delete role',
                    content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
                    maskClosable: false,
                    okText: 'Delete',
                    okType: 'danger',
                    cancelText: 'Cancel',
                    onOk: () =>
                        deleteRoleMutation.mutateAsync(record.id).then(
                            () => {
                                message.success('Role deleted');
                                if (selectedRoleId === record.id) {
                                    setPanelOpen(false);
                                    setSelectedRoleId(null);
                                }
                            },
                            (err) => {
                                confirmInstance.destroy();
                                const msg = err?.response?.data?.message || 'Failed to delete role';
                                Modal.error({
                                    title: 'Cannot delete role',
                                    content: msg,
                                    maskClosable: false,
                                    okText: 'OK',
                                });
                                throw err;
                            },
                        ),
                });
            }
            if (menuInfo.key === 'edit') {
                if (!canUpdate) return;
                setEditingRoleId(record.id);
                setModalOpen(true);
            }
        },
        [canUpdate, canDelete, deleteRoleMutation, selectedRoleId],
    );

    const closeModal = useCallback(() => {
        setModalOpen(false);
        setEditingRoleId(null);
        form.resetFields();
        setPermModuleTab(PERM_TAB_ALL);
        setPermSearch('');
        setModalSelectedPermissionIds([]);
        setSelectedPredefinedRoleName(null);
        setSearchText('');
    }, [form]);

    const closePanel = useCallback(() => {
        setPanelOpen(false);
        setSelectedRoleId(null);
    }, []);

    const openPanel = useCallback((id) => {
        setSelectedRoleId(id);
        setPanelOpen(true);
    }, []);

    const columns = useMemo(
        () => [
            { title: 'Role', dataIndex: 'name', key: 'name' },
            { title: 'Type', dataIndex: 'type', key: 'type' },
            {
                title: 'Created',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (v) => formatCreated(v),
            },
            {
                title: 'Last Updated',
                dataIndex: 'updatedAt',
                key: 'updatedAt',
                render: (v) => formatLastUpdated(v),
            },
            ...(canUpdate || canDelete
                ? [
                      {
                          title: 'Action',
                          key: 'action',
                          render: (_, record) => (
                              <span onClick={(e) => e.stopPropagation()}>
                                  <Dropdown
                                      menu={{
                                          items: [
                                              ...(canUpdate ? [{ key: 'edit', label: 'Edit' }] : []),
                                              ...(canDelete ? [{ key: 'delete', label: 'Delete', danger: true }] : []),
                                          ],
                                          onClick: (info) => handleActionClick(info, record),
                                      }}
                                      trigger={['click']}
                                  >
                                      <span style={{ cursor: 'pointer' }}>
                                          <MoreOutlined />
                                      </span>
                                  </Dropdown>
                              </span>
                          ),
                      },
                  ]
                : []),
        ],
        [canUpdate, canDelete, handleActionClick],
    );

    return {
        searchText,
        setSearchText,
        modalOpen,
        setModalOpen,
        panelOpen,
        closePanel,
        openPanel,
        selectedRoleId,
        selectedRoleName,
        tableData,
        columns,
        roles,
        form,
        handleCreateRole,
        permissionModules,
        permSliderTabItems,
        permModuleTab,
        setPermModuleTab,
        permSearch,
        setPermSearch,
        modalSelectedPermissionIds,
        setModalSelectedPermissionIds,
        filteredPermissionsForModal,
        modalPermissionTableData,
        permissionsLoading,
        canView,
        canCreate,
        canUpdate,
        createRoleMutation,
        updateRoleMutation,
        closeModal,
        handlePermissionSelectionChange,
        editingRoleId,
        setEditingRoleId,
        handleUpdateRole,
        predefinedRoleOptions,
        selectedPredefinedRoleName,
        applyPredefinedRole,
        toggleModalPermissionId,
        isRoleChanged,
    };
};
